"use client";

import { Combobox as ComboboxPrimitive } from "@base-ui/react/combobox";
import * as React from "react";
import type { VariantProps } from "../../lib/cva";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";
import { Icon } from "./icon";
import {
  SelectComboboxHostContext,
  selectTriggerIconClassName,
  selectTriggerVariants,
} from "./select";

// ---------------------------------------------------------------------------
// A filterable Select whose FIELD is the search: the trigger is a real text
// input driving the list's query — type-to-filter happens in the trigger
// itself, with no search row inside the popover. Single-select only: the
// input shows the selected label at rest and becomes the query while open
// (the selection's label steps back to the placeholder).
//
// Built on the Combobox primitive ("a filterable Select"), but it wears the
// Select trigger/popup chrome and — via `SelectComboboxHostContext` — hosts
// the same `SelectItem`/`SelectSeparator` children a plain `Select` does, so
// one options renderer serves both hosts. Filtering is external by design
// (`filter={null}`): the options renderer decides what to mount, reading the
// live query through `useSelectQuery`, and `SelectEmpty` reads the same
// predicate over the full `items` pool, so the two can never disagree.
// ---------------------------------------------------------------------------

/** One selectable row of the pool: the `value` a `SelectItem` carries and the
 * `label` the field shows for it. */
export type SelectComboboxOption = {
  value: string;
  label: string;
};

/** The live search query of an enclosing `SelectCombobox`. Lets grouped
 * option renderers collapse to a flat filtered list while searching (group
 * headers / separators would dangle over groups whose items all filter out). */
export type SelectQuery = {
  /** The raw text in the field while the popup is open. */
  query: string;
  /** Whether a non-blank query is filtering right now. */
  hasQuery: boolean;
  /** The shared match predicate (case-insensitive contains) — filter rows
   * with this so the no-matches state stays in agreement. */
  matches: (label: string) => boolean;
};

type SelectComboboxContextValue = SelectQuery & {
  items: readonly SelectComboboxOption[];
  open: boolean;
  selectedLabel: string | null;
  disabled: boolean;
  triggerRef: React.RefObject<HTMLElement | null>;
};

const SelectComboboxContext =
  React.createContext<SelectComboboxContextValue | null>(null);

// Hoisted so the no-host default is referentially stable across renders.
const NO_QUERY: SelectQuery = {
  query: "",
  hasQuery: false,
  matches: () => true,
};

/**
 * Read the live search query of the enclosing `SelectCombobox`. Safe to call
 * from an options renderer mounted in a plain `Select` too — with no host it
 * answers the stable "no query" state (match everything), so one renderer
 * serves both hosts unconditionally.
 */
export function useSelectQuery(): SelectQuery {
  const context = React.useContext(SelectComboboxContext);
  return context ?? NO_QUERY;
}

function useSelectComboboxContext(part: string): SelectComboboxContextValue {
  const context = React.useContext(SelectComboboxContext);
  if (!context) {
    throw new Error(`${part} must be rendered inside a SelectCombobox`);
  }
  return context;
}

export type SelectComboboxProps = {
  /** The selected option's `value`; `null` when nothing is selected. */
  value: string | null;
  onValueChange: (value: string | null) => void;
  /** The FULL selectable pool, unfiltered — labels drive the rest-state field
   * text, the shared match predicate, and the no-matches state. The options
   * renderer still decides what to mount; this list is never rendered. */
  items: readonly SelectComboboxOption[];
  children: React.ReactNode;
  disabled?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export function SelectCombobox({
  value,
  onValueChange,
  items,
  children,
  disabled = false,
  onOpenChange,
}: SelectComboboxProps): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const triggerRef = React.useRef<HTMLElement | null>(null);

  const labelByValue = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const item of items) {
      map.set(item.value, item.label);
    }
    return map;
  }, [items]);

  const selectedLabel =
    value !== null ? (labelByValue.get(value) ?? value) : null;

  const needle = query.trim().toLowerCase();
  const matches = React.useCallback(
    (label: string) => label.toLowerCase().includes(needle),
    [needle],
  );

  const context = React.useMemo<SelectComboboxContextValue>(
    () => ({
      query,
      hasQuery: needle.length > 0,
      matches,
      items,
      open,
      selectedLabel,
      disabled,
      triggerRef,
    }),
    [query, needle, matches, items, open, selectedLabel, disabled],
  );

  return (
    // No autoHighlight: it computes against the PREVIOUS render's mounted
    // rows (filtering is external, one render behind the primitive's view)
    // and invalidates when they unmount — keyboard selection is arrow-then-
    // Enter, the plain Select's grammar.
    <ComboboxPrimitive.Root
      disabled={disabled}
      // The options renderer is the filter: it mounts only matching rows
      // (via `useSelectQuery`), so the primitive must never disagree by
      // hiding rows itself.
      filter={null}
      // Every open starts from a BLANK query with the full list on offer —
      // the selection's label steps back to the placeholder (see the
      // trigger). The clear runs on CLOSE, not on open: type-to-open lands
      // the first keystroke's input-change alongside the open transition,
      // and an on-open clear would wipe it.
      inputValue={open ? query : (selectedLabel ?? "")}
      itemToStringLabel={(itemValue: string) =>
        labelByValue.get(itemValue) ?? itemValue
      }
      onInputValueChange={(next, details) => {
        // Only the user's own edits drive the query — selection commits and
        // programmatic syncs would otherwise flash the picked label (or the
        // rest-state value) into the filter.
        if (
          details.reason === "input-change" ||
          details.reason === "input-clear"
        ) {
          setQuery(next);
        }
      }}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) {
          setQuery("");
        }
        onOpenChange?.(nextOpen);
      }}
      onValueChange={(next) => onValueChange(next)}
      openOnInputClick
      value={value}
    >
      <SelectComboboxHostContext.Provider value={true}>
        <SelectComboboxContext.Provider value={context}>
          {children}
        </SelectComboboxContext.Provider>
      </SelectComboboxHostContext.Provider>
    </ComboboxPrimitive.Root>
  );
}

export type SelectComboboxTriggerProps = VariantProps<
  typeof selectTriggerVariants
> &
  Omittable<{
    /** Empty-value copy. While open, the CURRENT selection's label becomes
     * the placeholder instead (the field itself is now the query). */
    placeholder?: string;
    className?: string;
    /** The input's name. A placeholder is not a label, and a combobox takes
     *  no name from its value. */
    "aria-label"?: string;
  }>;

export function SelectComboboxTrigger({
  className,
  size = "default",
  variant = "default",
  width = "full",
  bleedStart = false,
  placeholder,
  "aria-label": ariaLabel,
}: SelectComboboxTriggerProps): React.ReactElement {
  const { open, selectedLabel, disabled, triggerRef } =
    useSelectComboboxContext("SelectComboboxTrigger");
  return (
    // A <label> so a click anywhere on the shell focuses the input natively —
    // the input is the control, the chrome is just its coat.
    <label
      className={cn(
        selectTriggerVariants({ size, variant, width, bleedStart }),
        // Focus lands on the inner input, so the trigger's own focus-visible
        // ring never fires — mirror it from within. Always-on for focus (a
        // text field shows its ring on any focus, not just keyboard).
        "cursor-text focus-within:border-ring focus-within:ring-[3px]",
        // The Field stamps `aria-invalid` on the input, not on this shell —
        // mirror that too, at the rungs the variants give a bare trigger.
        "has-aria-invalid:border-destructive/36 focus-within:has-aria-invalid:border-destructive/64 focus-within:has-aria-invalid:ring-destructive/16 dark:focus-within:has-aria-invalid:ring-destructive/24",
        className,
      )}
      data-disabled={disabled ? "" : undefined}
      // Re-stamped here so the trigger variants' popup-open wash applies —
      // the primitive only stamps the input.
      data-popup-open={open ? "" : undefined}
      data-slot="select-combobox-trigger"
      ref={(node) => {
        triggerRef.current = node;
      }}
    >
      <ComboboxPrimitive.Input
        aria-label={ariaLabel}
        className="min-w-0 flex-1 truncate bg-transparent outline-none placeholder:text-muted-foreground"
        data-slot="select-combobox-input"
        placeholder={open && selectedLabel !== null ? selectedLabel : placeholder}
      />
      <ComboboxPrimitive.Icon data-slot="select-icon">
        <Icon className={selectTriggerIconClassName} name="chevrons-up-down" />
      </ComboboxPrimitive.Icon>
    </label>
  );
}

export function SelectComboboxPopup({
  className,
  children,
  side = "bottom",
  sideOffset = 4,
  align = "start",
  alignOffset = 0,
  "aria-label": ariaLabel,
}: Omittable<{
  className?: string;
  children?: React.ReactNode;
  /** The list's name, when the trigger's name does not fit the options. */
  "aria-label"?: string;
  side?: ComboboxPrimitive.Positioner.Props["side"];
  sideOffset?: ComboboxPrimitive.Positioner.Props["sideOffset"];
  align?: ComboboxPrimitive.Positioner.Props["align"];
  alignOffset?: ComboboxPrimitive.Positioner.Props["alignOffset"];
}>): React.ReactElement {
  const { triggerRef } = useSelectComboboxContext("SelectComboboxPopup");
  return (
    <ComboboxPrimitive.Portal>
      <ComboboxPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        // Anchor the whole trigger shell, not the bare input inside it, so
        // the popup spans the field it belongs to.
        anchor={triggerRef}
        className="z-50 select-none"
        data-slot="select-positioner"
        side={side}
        sideOffset={sideOffset}
      >
        <ComboboxPrimitive.Popup
          className="origin-(--transform-origin) text-foreground outline-none"
          // Portalled, so it inherits no surface scope — names its own, and
          // descendant `border-border` draws the popover-tuned hairline.
          data-on="popover"
          data-slot="select-popup"
        >
          <div className="relative h-full min-w-(--anchor-width) rounded-lg border bg-popover not-dark:bg-clip-padding shadow-popover before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-lg)-1px)] before:shadow-[0_1px_--theme(--color-black/4%)] dark:before:shadow-[0_-1px_--theme(--color-white/6%)]">
            <ComboboxPrimitive.List
              aria-label={ariaLabel}
              className={cn(
                "max-h-(--available-height) overflow-y-auto p-1",
                className,
              )}
              data-slot="select-list"
            >
              {children}
            </ComboboxPrimitive.List>
          </div>
        </ComboboxPrimitive.Popup>
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  );
}

/**
 * The no-matches row. Judges emptiness with the SAME predicate the options
 * renderer filters with, over the full `items` pool, so the two can never
 * disagree. Always mounted; only its children come and go (`not-empty:`
 * carries the padding so the blank state takes no space).
 */
export function SelectEmpty({
  className,
  children,
}: Omittable<{
  className?: string;
  children?: React.ReactNode;
}>): React.ReactElement {
  const { items, matches } = useSelectComboboxContext("SelectEmpty");
  const empty = items.every((item) => !matches(item.label));
  return (
    <div
      className={cn(
        "not-empty:p-2 text-center text-base text-muted-foreground sm:text-sm",
        className,
      )}
      data-slot="select-empty"
    >
      {empty ? children : null}
    </div>
  );
}
